import SimpleLoadingSpinner from '@src/components/SimpleLoadingSpinner';
import SparkleIcon from '@src/svgs/SparkleIcon';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';

interface SpeakerHeadshotGridProps {
  /** Combined preview URLs (existing headshots + new file previews) */
  allHeadshotPreviews: string[];
  /** Number of previews that are already-persisted headshots */
  existingHeadshotsCount: number;
  /** Whether an upload is currently in progress */
  isUploadingHeadshot: boolean;
  /** Remove an existing (already-persisted) headshot by its index in the previews array */
  onRemoveExisting: (index: number) => void;
  /** Remove a newly-selected file by its index relative to newHeadshotFiles */
  onRemoveNew: (index: number) => void;
  /** Trigger the file-picker dialog */
  onAddClick: () => void;
}

/**
 * Headshot grid UI extracted from SpeakerFormModal.
 *
 * A "Headshot" field label over a scrollable row of headshot thumbnails with
 * remove overlays, a "+N" count badge when items overflow, and a dashed "+"
 * button to add new headshots.
 */
const SpeakerHeadshotGrid: React.FC<SpeakerHeadshotGridProps> = ({
  allHeadshotPreviews,
  existingHeadshotsCount,
  isUploadingHeadshot,
  onRemoveExisting,
  onRemoveNew,
  onAddClick,
}) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const [hasScrolled, setHasScrolled] = useState(false);

  if (allHeadshotPreviews.length > 0) {
    return (
      <HeadshotFormGroup>
        <HeadshotLabel>Headshot</HeadshotLabel>
        <HeadshotGrid ref={gridRef}>
          {allHeadshotPreviews.map((preview, index) => {
            const isExisting = index < existingHeadshotsCount;
            const hiddenCount = allHeadshotPreviews.length - 3;
            const showOverlay =
              index === 2 && allHeadshotPreviews.length >= 3 && !hasScrolled;
            return (
              <HeadshotPreviewContainer
                key={preview}
                onMouseEnter={
                  index >= 2
                    ? () => {
                        if (hasScrolled) return;
                        setHasScrolled(true);
                        gridRef.current?.scrollBy({
                          left: 48,
                          behavior: 'smooth',
                        });
                      }
                    : undefined
                }
              >
                <HeadshotImage
                  src={preview}
                  alt={`Speaker headshot ${index + 1}`}
                />
                {showOverlay && (
                  <HeadshotCountOverlay>
                    +{hiddenCount > 0 ? hiddenCount : ''}
                  </HeadshotCountOverlay>
                )}
                <HeadshotActions>
                  <HeadshotActionButton
                    type="button"
                    onClick={() =>
                      isExisting
                        ? onRemoveExisting(index)
                        : onRemoveNew(index - existingHeadshotsCount)
                    }
                  >
                    Remove
                  </HeadshotActionButton>
                </HeadshotActions>
              </HeadshotPreviewContainer>
            );
          })}
          <HeadshotPlaceholder
            type="button"
            aria-label="Add headshot"
            onClick={onAddClick}
          >
            <HeadshotPlaceholderIcon>+</HeadshotPlaceholderIcon>
          </HeadshotPlaceholder>
        </HeadshotGrid>
      </HeadshotFormGroup>
    );
  }

  return (
    <HeadshotFormGroup>
      <HeadshotLabel>Headshot</HeadshotLabel>
      <HeadshotPlaceholder
        type="button"
        aria-label="Add headshot"
        onClick={onAddClick}
      >
        {isUploadingHeadshot ? (
          <SparkleLoading />
        ) : (
          <HeadshotPlaceholderIcon>+</HeadshotPlaceholderIcon>
        )}
      </HeadshotPlaceholder>
    </HeadshotFormGroup>
  );
};

export default SpeakerHeadshotGrid;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const SparkleLoading = () => (
  <SimpleLoadingSpinner
    spinnerWidth={3.5}
    spinnerSize={24}
    icon={<SparkleIcon />}
    iconSize={10}
    text={null}
  />
);

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

// A fixed-width column at the right of the Name row, so neither the Name field
// nor this label moves as headshots are added. Left-aligned like every other
// field label.
const HEADSHOT_COLUMN_WIDTH = '178px';

const HeadshotFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-xs);
  width: ${HEADSHOT_COLUMN_WIDTH};
  flex-shrink: 0;
`;

// The same label the shared Input/Textarea primitives draw.
const HeadshotLabel = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-xs);
  color: var(--text-secondary);
`;

const HeadshotGrid = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  overflow-x: auto;
  overflow-y: hidden;
  width: ${HEADSHOT_COLUMN_WIDTH};
  padding-bottom: 4px;

  /* Thin scrollbar styling */
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--border);
    border-radius: 2px;
  }
  scrollbar-width: thin;
  scrollbar-color: var(--border) transparent;
`;

const HeadshotPreviewContainer = styled.div`
  position: relative;
  width: 54px;
  height: 54px;
  flex-shrink: 0;

  &:hover > div {
    opacity: 1;
  }
`;

const HeadshotImage = styled.img`
  width: 54px;
  height: 54px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--border);
`;

const HeadshotCountOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--foreground);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  z-index: 2;
`;

const HeadshotActions = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
`;

const HeadshotActionButton = styled.button`
  background: none;
  border: none;
  color: var(--foreground);
  font-size: 10px;
  font-weight: 500;
  cursor: pointer;
  padding: 1px 6px;

  &:hover {
    color: var(--primary);
  }
`;

const HeadshotPlaceholder = styled.button`
  width: 54px;
  height: 54px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px dashed var(--border);
  background: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color var(--transition-fast);

  /* Bordered-field hover escalation (docs/design-system.md → Form Fields). */
  &:hover {
    border-color: var(--text-muted);
  }
`;

const HeadshotPlaceholderIcon = styled.span`
  color: var(--text-muted);
  font-size: var(--text-lg);
  line-height: 1;
`;
